import { useState, useCallback } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import {
  UserPlus,
  X,
  Loader2,
  Phone,
  Mail,
  User,
  StickyNote,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface ManualLeadDialogProps {
  open: boolean;
  onClose: () => void;
}

interface LeadFormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes: string;
}

const INITIAL: LeadFormData = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  notes: "",
};

/**
 * Modal dialog for manually creating a new lead.
 *
 * Creates a contact with:
 *   contact_type = 'lead'
 *   lead_source  = 'manual'
 *   status       = 'cold'
 *
 * Also creates a contact_created event in the timeline.
 */
export function ManualLeadDialog({ open, onClose }: ManualLeadDialogProps) {
  const [form, setForm] = useState<LeadFormData>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();

  const update = (field: keyof LeadFormData, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = useCallback(async () => {
    if (!form.firstName.trim()) {
      notify("First name is required", { type: "warning" });
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      notify("Please enter a phone number or email", { type: "warning" });
      return;
    }

    setSaving(true);
    const now = new Date().toISOString();

    try {
      // Build contact data
      const contactData: Record<string, unknown> = {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        contact_type: "lead",
        lead_source: "manual",
        status: "cold",
        first_seen: now,
        last_seen: now,
        has_newsletter: false,
        tags: [],
        gender: "other",
        phone_jsonb: form.phone.trim()
          ? [{ number: form.phone.trim(), type: "Work" }]
          : [],
        email_jsonb: form.email.trim()
          ? [{ email: form.email.trim(), type: "Work" }]
          : [],
        phone_primary: form.phone.trim() || null,
        background: form.notes.trim(),
      };

      // Create the contact
      const { data: newContact } = await create(
        "contacts",
        { data: contactData },
        { returnPromise: true }
      );

      // Create timeline event
      if (newContact?.id) {
        await create(
          "events",
          {
            data: {
              contact_id: newContact.id,
              event_type: "contact_created",
              payload: {
                source: "manual",
                name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
              },
            },
          },
          { returnPromise: true }
        );
      }

      notify("Lead created successfully", { type: "success" });
      setForm(INITIAL);
      refresh();
      onClose();
    } catch (err) {
      console.error("Create lead failed:", err);
      notify(`Failed to create lead: ${(err as Error).message}`, {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [form, create, notify, refresh, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              New Lead
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Form */}
          <div className="p-4 space-y-3">
            {/* Name row */}
            <div className="flex gap-2">
              <label className="flex-1">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                  <User className="w-3 h-3" />
                  First name *
                </span>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                  placeholder="John"
                  autoFocus
                  className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </label>
              <label className="flex-1">
                <span className="text-xs font-medium text-muted-foreground mb-1 block">
                  Last name
                </span>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                  placeholder="Smith"
                  className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </label>
            </div>

            {/* Phone */}
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                <Phone className="w-3 h-3" />
                Phone
              </span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+15551234567"
                className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </label>

            {/* Email */}
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                <Mail className="w-3 h-3" />
                Email
              </span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="john@example.com"
                className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </label>

            {/* Notes */}
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
                <StickyNote className="w-3 h-3" />
                Notes
              </span>
              <textarea
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="How did they reach out? What do they need?"
                rows={2}
                className="w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </label>

            <p className="text-xs text-muted-foreground">
              * Phone or email required
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !form.firstName.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <UserPlus className="w-3.5 h-3.5" />
              )}
              {saving ? "Creating…" : "Create Lead"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
